import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  fetchApiDelete,
  fetchApiGet,
  fetchApiPost,
  fetchApiPut,
} from "./utils";

// Enums
export enum CertificateType {
  BirthdayFund = "birthday_fund",
}

// Validation Schemas
export const CreateTemplateValidation = z.object({
  location_name: z.string().min(1, "Location name is required"),
  template_data: z.record(z.any()),
  certificate_type: z.nativeEnum(CertificateType).optional().default(CertificateType.BirthdayFund),
});

export const UpdateTemplateValidation = z.object({
  location_name: z.string().min(1, "Location name is required"),
  template_data: z.record(z.any()),
  certificate_type: z.nativeEnum(CertificateType),
});

export const CreateInstanceValidation = z.object({
  certificate_id: z.string().min(1, "Certificate ID is required"),
  location_name: z.string().min(1, "Location name is required"),
  template_id: z.string().uuid().optional(),
});

// Type Definitions
export interface CertificateTemplate {
  id: string;
  location_name: string;
  template_data: Record<string, any>;
  certificate_type: CertificateType;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  created_by_user_id: string;
}

export interface CertificateInstance {
  id: string;
  certificate_id: string;
  location_name: string;
  document_id: string;
  template_id: string | null;
  created_at: string;
  deleted_at: string | null;
  created_by_user_id: string;
}

// Response Types
export interface CreateTemplateResponse {
  template: CertificateTemplate;
}

export interface ListTemplatesResponse {
  templates: CertificateTemplate[];
}

export interface GetTemplateResponse {
  template: CertificateTemplate;
}

export interface UpdateTemplateResponse {
  template: CertificateTemplate;
}

export interface DeleteTemplateResponse {
  template: CertificateTemplate;
}

export interface CreateInstanceResponse {
  instance: CertificateInstance;
  presigned_put_url: string;
}

export interface ListInstancesResponse {
  instances: CertificateInstance[];
}

export interface GetInstanceResponse {
  instance: CertificateInstance;
}

export interface DeleteInstanceResponse {
  instance: CertificateInstance;
}

export interface GetCertificateAssetResponse {
  presigned_get_url: string;
}

// Template Hooks
export function useListCertificateTemplates() {
  return useQuery({
    queryKey: ["birthday", "certificate", "templates"],
    queryFn: () => fetchApiGet<ListTemplatesResponse>("fund/birthday/certificate/templates"),
    retry: 1,
  });
}

export function useGetCertificateTemplate(templateId?: string | null) {
  return useQuery({
    queryKey: ["birthday", "certificate", "template", templateId],
    queryFn: () => fetchApiGet<GetTemplateResponse>(`fund/birthday/certificate/template/${templateId}`),
    retry: 1,
    enabled: !!templateId,
  });
}

export function useCreateCertificateTemplate() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CreateTemplateValidation>) => {
    const result = await fetchApiPost<CreateTemplateResponse>(
      "fund/birthday/certificate/template",
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["birthday", "certificate", "templates"] });
    }

    return result;
  };
}

export function useUpdateCertificateTemplate() {
  const queryClient = useQueryClient();

  return async (templateId: string, data: z.infer<typeof UpdateTemplateValidation>) => {
    const result = await fetchApiPut<UpdateTemplateResponse>(
      `fund/birthday/certificate/template/${templateId}`,
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["birthday", "certificate", "templates"] });
      queryClient.invalidateQueries({ queryKey: ["birthday", "certificate", "template", templateId] });
    }

    return result;
  };
}

export function useDeleteCertificateTemplate() {
  const queryClient = useQueryClient();

  return async (templateId: string) => {
    const result = await fetchApiDelete<DeleteTemplateResponse>(
      `fund/birthday/certificate/template/${templateId}`
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["birthday", "certificate", "templates"] });
      queryClient.invalidateQueries({ queryKey: ["birthday", "certificate", "template", templateId] });
    }

    return result;
  };
}

// Instance Hooks
export function useListCertificateInstances() {
  return useQuery({
    queryKey: ["birthday", "certificate", "instances"],
    queryFn: () => fetchApiGet<ListInstancesResponse>("fund/birthday/certificate/instances"),
    retry: 1,
  });
}

export function useGetCertificateInstance(instanceId?: string | null) {
  return useQuery({
    queryKey: ["birthday", "certificate", "instance", instanceId],
    queryFn: () => fetchApiGet<GetInstanceResponse>(`fund/birthday/certificate/instance/${instanceId}`),
    retry: 1,
    enabled: !!instanceId,
  });
}

export function useCreateCertificateInstance() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CreateInstanceValidation>) => {
    const result = await fetchApiPost<CreateInstanceResponse>(
      "fund/birthday/certificate/instance",
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["birthday", "certificate", "instances"] });
    }

    return result;
  };
}

export function useDeleteCertificateInstance() {
  const queryClient = useQueryClient();

  return async (instanceId: string) => {
    const result = await fetchApiDelete<DeleteInstanceResponse>(
      `fund/birthday/certificate/instance/${instanceId}`
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["birthday", "certificate", "instances"] });
      queryClient.invalidateQueries({ queryKey: ["birthday", "certificate", "instance", instanceId] });
    }

    return result;
  };
}

// Certificate Asset Hook
export function useGetCertificateAsset() {
  return useQuery({
    queryKey: ["birthday", "certificate", "asset"],
    queryFn: () => fetchApiGet<GetCertificateAssetResponse>("fund/birthday/certificate/asset"),
    retry: 1,
  });
}
